/**
 * Global last-resort error reporter (diagnostics).
 *
 * React error boundaries only catch render/lifecycle throws inside their own
 * subtree. Errors that escape every boundary — async callbacks, timers, or a
 * throw rethrown above the React root — are rethrown to the window and unmount
 * the whole SPA, leaving only the fixed VONK background: the "inescapable orange
 * screen" with no clue about what failed.
 *
 * This installs window-level `error` + `unhandledrejection` handlers that, on a
 * fatal uncaught error, log structured details to the console and paint a
 * framework-free overlay. The overlay is built with raw DOM (no React) so it
 * survives even after the SPA has unmounted, turning the silent orange screen
 * into the actual error that can be screenshotted/reported.
 */

export interface ClientErrorDetail {
  /** The error message (unminified even in a production build). */
  message: string
  /** Stacktrace, or a placeholder when none is available. */
  stack: string
  /** Where the report came from: 'window.onerror' or 'unhandledrejection'. */
  source: string
  /** In-app route (pathname + search) at the time of the crash. */
  route: string
  /** Full URL, for cross-referencing. */
  href: string
  /** Browser UA string. */
  userAgent: string
  /** ISO timestamp of the report. */
  at: string
}

const OVERLAY_ID = 'vonk-error-overlay'

/**
 * Known-benign messages that must never raise the overlay: ResizeObserver loop
 * notifications (fired by layout libs, harmless), fetch/navigation aborts, and
 * the opaque "Script error." the browser substitutes for a throw inside a script
 * from another origin. Our own bundle is same-origin and therefore always yields
 * a real message and stack, so anything reduced to "Script error." comes from
 * outside the app — a browser extension, for instance — and holds nothing to act
 * on. Anchored, so a genuine error merely starting with those words still shows.
 */
const IGNORED_PATTERNS = [/ResizeObserver loop/i, /AbortError/i, /^Script error\.?$/i]

let installed = false
let onError: ((event: ErrorEvent) => void) | null = null
let onRejection: ((event: PromiseRejectionEvent) => void) | null = null

// Dedupe by message so a retry storm cannot stack overlays or spam the console.
const seen = new Set<string>()

/** Whether a given error message is worth surfacing (filters benign noise). */
export function shouldReport(message: string): boolean {
  if (!message) return false
  return !IGNORED_PATTERNS.some((re) => re.test(message))
}

function toDetail(error: unknown, source: string): ClientErrorDetail {
  const err = error instanceof Error ? error : undefined
  const message = err?.message ?? (typeof error === 'string' ? error : String(error))
  const hasLocation = typeof location !== 'undefined'
  return {
    message,
    stack: err?.stack ?? '(geen stacktrace beschikbaar)',
    source,
    route: hasLocation ? location.pathname + location.search : '',
    href: hasLocation ? location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    at: new Date().toISOString(),
  }
}

/** Render the detail as the plain-text block shown in the overlay and copied. */
export function formatErrorDetail(detail: ClientErrorDetail): string {
  return [
    `Bericht: ${detail.message}`,
    `Bron: ${detail.source}`,
    `Route: ${detail.route}`,
    `URL: ${detail.href}`,
    `Tijd: ${detail.at}`,
    `User-Agent: ${detail.userAgent}`,
    '',
    'Stacktrace:',
    detail.stack,
  ].join('\n')
}

function renderOverlay(detail: ClientErrorDetail): void {
  if (typeof document === 'undefined' || document.getElementById(OVERLAY_ID)) return
  const text = formatErrorDetail(detail)

  const overlay = document.createElement('div')
  overlay.id = OVERLAY_ID
  overlay.setAttribute('role', 'alertdialog')
  overlay.setAttribute('aria-label', 'Foutdetails')
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;' +
    'justify-content:center;padding:24px;background:rgba(17,17,21,0.95);color:#f5f5f5;' +
    'font-family:system-ui,-apple-system,sans-serif;overflow:auto;'

  const card = document.createElement('div')
  card.style.cssText =
    'max-width:760px;width:100%;background:#1e1e26;border:1px solid rgba(255,255,255,0.12);' +
    'border-radius:12px;padding:20px 22px;box-shadow:0 10px 40px rgba(0,0,0,0.5);'

  const heading = document.createElement('h1')
  heading.textContent = 'Er ging iets mis'
  heading.style.cssText = 'margin:0 0 6px;font-size:18px;font-weight:600;'

  const intro = document.createElement('p')
  intro.textContent =
    'De applicatie liep tegen een onverwachte fout aan. Maak hiervan een ' +
    "schermafbeelding (of klik 'Kopieer details') en stuur die door, zodat we de " +
    'oorzaak kunnen oplossen.'
  intro.style.cssText = 'margin:0 0 14px;font-size:13px;line-height:1.5;color:#c7c7d1;'

  const pre = document.createElement('pre')
  pre.textContent = text
  pre.style.cssText =
    'margin:0 0 14px;padding:12px;background:#11111a;border-radius:8px;' +
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px;' +
    'line-height:1.45;white-space:pre-wrap;word-break:break-word;max-height:40vh;' +
    'overflow:auto;color:#ffd9c2;'

  const actions = document.createElement('div')
  actions.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;'

  const copyBtn = document.createElement('button')
  copyBtn.type = 'button'
  copyBtn.textContent = 'Kopieer details'
  copyBtn.style.cssText =
    'appearance:none;border:0;border-radius:8px;padding:8px 14px;font-size:13px;' +
    'font-weight:600;cursor:pointer;background:#f47c20;color:#1a1209;'
  copyBtn.addEventListener('click', () => {
    const done = () => {
      copyBtn.textContent = 'Gekopieerd ✓'
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, () => {})
    } else {
      done()
    }
  })

  const homeBtn = document.createElement('button')
  homeBtn.type = 'button'
  homeBtn.textContent = 'Terug naar start'
  homeBtn.style.cssText =
    'appearance:none;border:1px solid rgba(255,255,255,0.2);border-radius:8px;' +
    'padding:8px 14px;font-size:13px;font-weight:600;cursor:pointer;' +
    'background:transparent;color:#f5f5f5;'
  homeBtn.addEventListener('click', () => {
    // A hard navigation escapes even when the in-memory router is gone.
    window.location.assign('/')
  })

  actions.append(copyBtn, homeBtn)
  card.append(heading, intro, pre, actions)
  overlay.append(card)
  document.body.append(overlay)
}

function report(detail: ClientErrorDetail): void {
  if (seen.has(detail.message)) return
  seen.add(detail.message)
  console.error('[client-error]', detail)
  renderOverlay(detail)
}

/**
 * Install the global handlers. Idempotent and safe to call once at startup
 * (before React mounts). No-op outside a browser (SSR/test guards).
 */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  onError = (event: ErrorEvent) => {
    const detail = toDetail(event.error ?? event.message, 'window.onerror')
    if (shouldReport(detail.message)) report(detail)
  }
  onRejection = (event: PromiseRejectionEvent) => {
    const detail = toDetail(event.reason, 'unhandledrejection')
    if (shouldReport(detail.message)) report(detail)
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
}

/** Test-only: remove handlers, clear dedupe state and any rendered overlay. */
export function __resetErrorReporterForTests(): void {
  if (onError) window.removeEventListener('error', onError)
  if (onRejection) window.removeEventListener('unhandledrejection', onRejection)
  onError = null
  onRejection = null
  installed = false
  seen.clear()
  document.getElementById(OVERLAY_ID)?.remove()
}
